import React, { useState, useEffect, useCallback } from 'react';
// We'll use alert instead of toast for now
import { useDispatch, useSelector } from 'react-redux';
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { RootState, AppDispatch } from '../../store/store';
import { setCurrentIndex, createProjectAsync, updateProjectAsync, deleteProjectAsync, fetchProject } from '../../store/projectSlice';
import { AVAILABLE_MODELS, BEDROCK_MODELS, AvailableModel, BedrockModel } from '../../constants/defaults';
import { exportProjectSettings, importProjectSettingsFromFile } from '../../utils/ProjectSettingsUtils';
import RagSettingsPanel from './RagSettingsPanel';
import EditProjectDialog from './EditProjectDialog';
import ImportSettingsDialog from './ImportSettingsDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import styles from './ProjectHeader.module.css';

const ProjectHeader: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector((state: RootState) => state.projects.selectedProject);
  const currentIndex = useSelector((state: RootState) => state.projects.currentIndex);
  const loading = useSelector((state: RootState) => state.projects.loading);
  const [isEditing, setIsEditing] = useState(false);
  const [confirmImportSettings, setConfirmImportSettings] = useState(false);
  const [confirmDeleteProject, setConfirmDeleteProject] = useState(false);
  const [importedSettings, setImportedSettings] = useState<Record<string, string | number> | null>(null);
  const [editedName, setEditedName] = useState('');
  const [editedBasePrompt, setEditedBasePrompt] = useState('');
  const [editedIntroMessage, setEditedIntroMessage] = useState('');
  const [editedModel, setEditedModel] = useState<AvailableModel | ''>('');
  const [editedChatType, setEditedChatType] = useState<'direct' | 'rag'>('direct');
  const [editedBedrockModel, setEditedBedrockModel] = useState<BedrockModel | ''>('');
  const [editedRagVectorDb, setEditedRagVectorDb] = useState('');

  useEffect(() => {
    if (project) {
      setEditedName(project.name || '');
      setEditedBasePrompt(project.base_prompt || '');
      setEditedIntroMessage(project.intro_message || '');
      setEditedModel(
        AVAILABLE_MODELS.some(m => m.value === project.model_name)
          ? project.model_name as AvailableModel
          : AVAILABLE_MODELS[0].value
      );
      setEditedChatType(project.chat_type || 'direct');
      setEditedBedrockModel(
        BEDROCK_MODELS.some(m => m.value === project.model_name)
          ? project.model_name as BedrockModel
          : BEDROCK_MODELS[0].value
      );
      
      // Always initialize RAG vector DB from project if available
      if (project.chat_type === 'rag' && project.rag_vector_db) {
        setEditedRagVectorDb(project.rag_vector_db);
      } else if (project.chat_type !== 'rag') {
        setEditedRagVectorDb(currentIndex || '');
      }
    }
  }, [project, currentIndex]);

  // This useEffect ensures that editedChatType stays consistent when selecting an index
  useEffect(() => {
    // Log when editedChatType changes
    console.log('editedChatType changed to:', editedChatType);
  }, [editedChatType]);

  if (!project) return null;
  
  // Initialize form state when editing
  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async (settings: {
    name: string;
    base_prompt: string;
    intro_message: string;
    model_name: string;
    chat_type: 'direct' | 'rag';
    rag_vector_db?: string;
    rag_top_k?: number;
    rag_confidence_threshold?: number;
    rag_token_limit?: number;
  }) => {
    if (!project?.id) return;
    
    try {
      // Create updated project object with only the fields that should be updated
      const updatedProject = {
        id: project.id,
        project: settings
      };
      
      // Send updated project to backend
      await dispatch(updateProjectAsync(updatedProject));
      
      // Update Redux state for UI-specific fields
      if (settings.chat_type === 'rag' && settings.rag_vector_db && settings.rag_vector_db !== currentIndex) {
        dispatch(setCurrentIndex(settings.rag_vector_db));
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update project:', error);
    }
  };

  const handleDelete = () => {
    setConfirmDeleteProject(true);
  };

  const handleDuplicate = async () => {
    if (!project) return;
    
    // Create a new project with a copy of the current settings
    const duplicatedProject = {
      name: `${project.name} (Copy)`,
      base_prompt: project.base_prompt,
      intro_message: project.intro_message,
      model_name: project.model_name,
      chat_type: project.chat_type,
      rag_vector_db: project.rag_vector_db
    };
    
    // Add RAG-specific properties if they exist
    if (project.chat_type === 'rag') {
      Object.assign(duplicatedProject, {
        rag_confidence_threshold: project.rag_confidence_threshold,
        rag_top_k: project.rag_top_k,
        rag_token_limit: project.rag_token_limit
      });
    }

    const result = await dispatch(createProjectAsync(duplicatedProject));
    
    // If project creation successful, switch to the new project
    if (createProjectAsync.fulfilled.match(result) && result.payload.id) {
      await dispatch(fetchProject(result.payload.id));
      
      // Show success message
      alert('Project duplicated successfully');
    }
  };

  const handleCancel = () => {
    // Reset to the project's current values
    if (project) {
      setEditedName(project.name || '');
      setEditedBasePrompt(project.base_prompt || '');
      setEditedIntroMessage(project.intro_message || '');
      setEditedModel(
        AVAILABLE_MODELS.some(m => m.value === project.model_name)
          ? project.model_name as AvailableModel
          : AVAILABLE_MODELS[0].value
      );
      setEditedChatType(project.chat_type || 'direct');
      setEditedBedrockModel(
        BEDROCK_MODELS.some(m => m.value === project.model_name)
          ? project.model_name as BedrockModel
          : BEDROCK_MODELS[0].value
      );
    }
    setIsEditing(false);
  };

  const handleExportSettings = () => {
    exportProjectSettings(project);
  };

  const handleImportSettings = (settings: Record<string, string | number>) => {
    setImportedSettings(settings);
    setConfirmImportSettings(true);
  };

  const handleImportSettingsConfirm = () => {
    if (!importedSettings) return;

    // First set the chat type to ensure proper UI updates
    if (importedSettings.chat_type) {
      const chatType = importedSettings.chat_type as string;
      setEditedChatType(chatType === 'rag' ? 'rag' : 'direct');
    }

    // Apply settings to form
    if (importedSettings.name) {
      // Prepend "Imported" to project name to distinguish it from original
      const originalName = importedSettings.name as string;
      setEditedName(`Imported ${originalName}`);
    }
    if (importedSettings.base_prompt) setEditedBasePrompt(importedSettings.base_prompt as string);
    if (importedSettings.intro_message) setEditedIntroMessage(importedSettings.intro_message as string);
    
    // Handle model selection based on chat type
    if (importedSettings.model_name) {
      const modelName = importedSettings.model_name as string;
      const chatType = importedSettings.chat_type as string || 'direct';
      
      if (chatType === 'direct') {
        // For direct chat, try to find the model in AVAILABLE_MODELS
        const isValidModel = AVAILABLE_MODELS.some(m => m.value === modelName);
        if (isValidModel) {
          setEditedModel(modelName as any);
        }
      } else if (chatType === 'rag') {
        // For RAG chat, try to find the model in BEDROCK_MODELS
        const isValidModel = BEDROCK_MODELS.some(m => m.value === modelName);
        if (isValidModel) {
          setEditedBedrockModel(modelName as any);
        }
      }
    }

    if (importedSettings.rag_vector_db) {
      // Store the imported vector DB name for the RagSettingsPanel to validate
      const vectorDbName = importedSettings.rag_vector_db as string;
      console.log(`Setting imported vector DB: ${vectorDbName}`);
      setEditedRagVectorDb(vectorDbName);
    } else if (importedSettings.chat_type === 'rag') {
      // For RAG projects without a specified vector DB
      console.log('RAG project without vector DB specified');
      setEditedRagVectorDb('');
    } else {
      // Non-RAG projects don't need a vector DB
      setEditedRagVectorDb('');
    }

    setConfirmImportSettings(false);
    setImportedSettings(null);
  };

  return (
    <header className={styles.header}>
      <div className={styles.projectInfo}>
        <div className={styles.mainInfo}>
          <h1 className={styles.projectName}>{project.name}</h1>
          <span className={styles.modelBadge}>
            {project.chat_type === 'rag' 
              ? BEDROCK_MODELS.find(m => m.value === project.model_name)?.label || project.model_name 
              : AVAILABLE_MODELS.find(m => m.value === project.model_name)?.label || project.model_name
            }
          </span>
          {project.chat_type === 'rag' && <span className={styles.ragBadge}>RAG</span>}
        </div>
        <div className={styles.actions}>
          {/* Message export/import buttons removed - functionality moved to ChatInterface */}
          <Tooltip title="Edit Project">
            <Box component="span">
              <IconButton
                className={styles.iconButton}
                onClick={handleEdit}
                disabled={loading}
                size="small"
                color="default"
              >
                <EditIcon />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Delete Project">
            <Box component="span">
              <IconButton
                className={styles.iconButton}
                onClick={handleDelete}
                disabled={loading}
                size="small"
                color="default"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Duplicate Project">
            <Box component="span">
              <IconButton
                className={styles.iconButton}
                onClick={handleDuplicate}
                disabled={loading}
                size="small"
                color="default"
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
          </Tooltip>
        </div>
      </div>

      <EditProjectDialog
        open={isEditing}
        loading={loading}
        editedName={editedName}
        editedBasePrompt={editedBasePrompt}
        editedIntroMessage={editedIntroMessage}
        editedModel={editedModel}
        editedChatType={editedChatType}
        editedBedrockModel={editedBedrockModel}
        editedRagVectorDb={editedRagVectorDb}
        isEditing={isEditing}
        onClose={handleCancel}
        onSave={handleSave}
        onExportSettings={handleExportSettings}
        onImportSettings={handleImportSettings}
        onNameChange={(value) => setEditedName(value)}
        onBasePromptChange={(value) => setEditedBasePrompt(value)}
        onIntroMessageChange={(value) => setEditedIntroMessage(value)}
        onModelChange={(value) => setEditedModel(value)}
        onChatTypeChange={(value) => setEditedChatType(value)}
        onBedrockModelChange={(value) => setEditedBedrockModel(value)}
        onRagVectorDbChange={(value) => {
          setEditedRagVectorDb(value);
          
          // Ensure we're still in RAG mode (defensive programming)
          if (editedChatType !== 'rag') {
            console.log('Ensuring chat type is RAG');
            setEditedChatType('rag');
          }
          
          // Only update Redux store when not in editing mode
          if (!isEditing) {
            dispatch(setCurrentIndex(value));
          }
        }}
      />

      {/* Settings import confirmation dialog */}
      <ImportSettingsDialog
        open={confirmImportSettings}
        onClose={() => setConfirmImportSettings(false)}
        onConfirm={handleImportSettingsConfirm}
      />

      {/* Delete confirmation dialog */}
      <DeleteConfirmationDialog
        open={confirmDeleteProject}
        projectName={project?.name || ''}
        onClose={() => setConfirmDeleteProject(false)}
        onConfirm={() => {
          if (project?.id) {
            dispatch(deleteProjectAsync(project.id));
          }
          setConfirmDeleteProject(false);
        }}
      />
    </header>
  );
};

export default ProjectHeader;
